import { PRESIGNED_URL_AUTH_HEADER } from '@rentguru/commons-utils';
import { isNil } from 'lodash';

import { getAuthToken } from 'src/hooks/UserContext';

const PRESIGNED_URL_ENDPOINT = '/api/presigned-url';
const MAX_CONCURRENT_PRESIGNED_URL_REQUESTS = 6;
const DOWNLOAD_URL_CACHE_TTL_IN_MS = 10 * 60 * 1000;

export interface TransferProgress {
  transferredBytes: number;
  totalBytes?: number;
}

interface StorageOptions {
  contentDisposition?: string;
  contentType?: string;
  metadata?: Record<string, string>;
  onProgress?: (progress: TransferProgress) => void;
}

type PresignedUrlOperation = 'upload' | 'download' | 'delete';

interface CachedDownloadUrl {
  url: string;
  expiresAtInMs: number;
}

const downloadUrlCache = new Map<string, CachedDownloadUrl>();
const pendingDownloadUrlRequests = new Map<string, Promise<string>>();

let activeRequestCount = 0;
const queuedRequestStarters: (() => void)[] = [];

const acquireRequestSlot = async (): Promise<void> => {
  if (activeRequestCount < MAX_CONCURRENT_PRESIGNED_URL_REQUESTS) {
    activeRequestCount += 1;
    return;
  }
  await new Promise<void>((resolve) => queuedRequestStarters.push(resolve));
  activeRequestCount += 1;
};

const releaseRequestSlot = () => {
  activeRequestCount -= 1;
  const startNextRequest = queuedRequestStarters.shift();
  if (!isNil(startNextRequest)) {
    startNextRequest();
  }
};

const requestPresignedUrl = async (
  key: string,
  operation: PresignedUrlOperation,
  contentType?: string
): Promise<string> => {
  const token = await getAuthToken();
  if (isNil(token)) {
    throw new Error('Not authenticated');
  }

  await acquireRequestSlot();
  try {
    const response = await fetch(PRESIGNED_URL_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        [PRESIGNED_URL_AUTH_HEADER]: `Bearer ${token}`,
      },
      body: JSON.stringify({ key, operation, contentType }),
    });

    if (!response.ok) {
      throw new Error(`Failed to get pre-signed URL: ${response.statusText}`);
    }

    const { url } = await response.json();
    return url;
  } finally {
    releaseRequestSlot();
  }
};

const getCachedDownloadUrl = (key: string): string | null => {
  const cachedDownloadUrl = downloadUrlCache.get(key);
  if (isNil(cachedDownloadUrl) || performance.now() >= cachedDownloadUrl.expiresAtInMs) {
    return null;
  }
  return cachedDownloadUrl.url;
};

const getPresignedUrl = async (
  key: string,
  operation: PresignedUrlOperation,
  contentType?: string
): Promise<string> => {
  if (operation !== 'download') {
    return requestPresignedUrl(key, operation, contentType);
  }

  const cachedUrl = getCachedDownloadUrl(key);
  if (!isNil(cachedUrl)) {
    return cachedUrl;
  }

  const pendingRequest = pendingDownloadUrlRequests.get(key);
  if (!isNil(pendingRequest)) {
    return pendingRequest;
  }

  const request = requestPresignedUrl(key, operation, contentType)
    .then((url) => {
      downloadUrlCache.set(key, { url, expiresAtInMs: performance.now() + DOWNLOAD_URL_CACHE_TTL_IN_MS });
      return url;
    })
    .finally(() => {
      pendingDownloadUrlRequests.delete(key);
    });
  pendingDownloadUrlRequests.set(key, request);
  return request;
};

/**
 * S3 serves `<img>`/`<a download>` requests without CORS headers, because those requests carry no `Origin`
 * (and the response then carries no `Vary: Origin` either). Reusing such a cached response for a `fetch()`
 * makes the browser fail the CORS check, so every object fetch has to bypass the HTTP cache.
 */
export const fetchStorageObject = (url: string, init?: RequestInit): Promise<Response> =>
  fetch(url, { ...init, cache: 'reload' });

export const fetchStorageObjectAsBlob = async (url: string): Promise<Blob | null> => {
  const response = await fetchStorageObject(url);
  return response.ok ? response.blob() : null;
};

export const readBlobAsDataUri = (blob: Blob): Promise<string | null> =>
  new Promise<string | null>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });

/**
 * Get a download URL for a file.
 * Replaces: `getUrl({ path, options: { expiresIn } })` from aws-amplify/storage
 */
export async function getUrl({
  key,
  options,
}: {
  key: string;
  options?: { expiresIn?: number; validateObjectExistence?: boolean };
}) {
  const url = await getPresignedUrl(key, 'download');
  if (options?.validateObjectExistence) {
    const response = await fetchStorageObject(url, { method: 'HEAD' });
    if (!response.ok) {
      throw new Error(`Object does not exist: ${key}`);
    }
  }
  return { url: new URL(url) };
}

/**
 * Get file properties/metadata.
 * Replaces: `getProperties({ path })` from aws-amplify/storage
 */
export async function getProperties({ key }: { key: string }) {
  // Use a HEAD request on the download URL to get metadata
  const url = await getPresignedUrl(key, 'download');
  const response = await fetchStorageObject(url, { method: 'HEAD' });
  return {
    contentType: response.headers.get('content-type') ?? undefined,
    size: parseInt(response.headers.get('content-length') ?? '0', 10),
    lastModified: response.headers.get('last-modified') ? new Date(response.headers.get('last-modified')!) : undefined,
    metadata: {} as Record<string, string>,
  };
}

/**
 * Download file data.
 * Replaces: `downloadData({ path })` from aws-amplify/storage
 */
export function downloadData({ key }: { key: string }) {
  const resultPromise = (async () => {
    const url = await getPresignedUrl(key, 'download');
    const response = await fetchStorageObject(url);
    if (!response.ok) throw new Error(`Download failed: ${response.statusText}`);
    const blob = await response.blob();
    return {
      body: {
        blob: async () => blob,
        text: async () => await blob.text(),
      },
      lastModified: response.headers.get('last-modified')
        ? new Date(response.headers.get('last-modified')!)
        : undefined,
      metadata: {} as Record<string, string>,
    };
  })();

  return { result: resultPromise };
}

/**
 * Upload file data.
 * Replaces: `uploadData({ path, data, options })` from aws-amplify/storage
 */
export function uploadData({
  key,
  data,
  options,
}: {
  key: string;
  data: ArrayBuffer | Blob | string;
  options?: StorageOptions;
}) {
  const resultPromise = (async () => {
    const contentType = options?.contentType ?? 'application/octet-stream';
    const url = await getPresignedUrl(key, 'upload', contentType);
    const headers: Record<string, string> = {
      'Content-Type': contentType,
      ...(options?.contentDisposition ? { 'Content-Disposition': options.contentDisposition } : {}),
    };

    if (!isNil(options?.onProgress)) {
      await putWithProgress(url, data, headers, options.onProgress);
      return { key };
    }

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: data,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    return { key };
  })();

  return { result: resultPromise };
}

const putWithProgress = (
  url: string,
  data: ArrayBuffer | Blob | string,
  headers: Record<string, string>,
  onProgress: (progress: TransferProgress) => void
): Promise<void> =>
  new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', url);
    Object.entries(headers).forEach(([headerName, headerValue]) => {
      request.setRequestHeader(headerName, headerValue);
    });
    request.upload.onprogress = (progressEvent) => {
      onProgress({
        transferredBytes: progressEvent.loaded,
        totalBytes: progressEvent.lengthComputable ? progressEvent.total : undefined,
      });
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed: ${request.statusText}`));
      }
    };
    request.onerror = () => reject(new Error('Upload failed: network error'));
    request.send(data);
  });

/**
 * Remove (delete) a file.
 * Replaces: `remove({ path })` from aws-amplify/storage
 */
export async function remove({ key }: { key: string }) {
  const url = await getPresignedUrl(key, 'delete');
  const response = await fetch(url, { method: 'DELETE' });
  if (!response.ok) {
    throw new Error(`Delete failed: ${response.statusText}`);
  }
  downloadUrlCache.delete(key);
  return { key };
}
